import React, { useState, useRef, useEffect } from 'react';
import { FileImage } from 'lucide-react';

interface ImageThumbnailProps {
  filePath: string;
  fileName: string;
  downloadFn: (filePath: string) => Promise<Blob>;
  loadImmediately?: boolean;
}

const ImageThumbnail: React.FC<ImageThumbnailProps> = ({ filePath, fileName, downloadFn, loadImmediately = false }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hasRequestedRef = useRef(false);
  const imageUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    let observer: IntersectionObserver | null = null;

    // Reset state when filePath changes
    hasRequestedRef.current = false;
    setLoading(true);
    setError(false);
    if (imageUrlRef.current) {
      window.URL.revokeObjectURL(imageUrlRef.current);
      imageUrlRef.current = null;
      setImageUrl(null);
    }

    const loadImage = async () => {
      if (hasRequestedRef.current) return;
      hasRequestedRef.current = true;
      
      try {
        if (!filePath || filePath.trim() === '') {
          throw new Error('File path is empty');
        }
        console.log('Loading image:', filePath);
        const blob = await downloadFn(filePath);
        if (!isMounted) {
          console.log('Component unmounted, skipping image load');
          return;
        }
        if (!blob || blob.size === 0) {
          throw new Error('Received empty blob');
        }
        const url = window.URL.createObjectURL(blob);
        imageUrlRef.current = url;
        setImageUrl(url);
        setLoading(false);
        console.log('Image loaded successfully:', filePath, 'Size:', blob.size);
      } catch (err: any) {
        console.error('Failed to load thumbnail:', filePath, err);
        console.error('Error details:', {
          message: err?.message,
          response: err?.response?.data,
          status: err?.response?.status,
          statusText: err?.response?.statusText
        });
        if (isMounted) {
          setError(true);
          setLoading(false);
        }
      }
    };

    // If loadImmediately is true, load right away without waiting for ref
    if (loadImmediately) {
      loadImage();
      
      return () => {
        isMounted = false;
        if (imageUrlRef.current) {
          window.URL.revokeObjectURL(imageUrlRef.current);
          imageUrlRef.current = null;
        }
      };
    } else {
      // Use setTimeout to ensure ref is attached
      const checkAndLoad = () => {
        if (!containerRef.current || hasRequestedRef.current) return;
        
        // Check if element is already visible
        const rect = containerRef.current.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
        
        if (isVisible) {
          // Load immediately if already visible
          loadImage();
          return;
        }
        
        // Otherwise use IntersectionObserver
        observer = new IntersectionObserver((entries) => {
          const entry = entries[0];
          if (entry.isIntersecting && !hasRequestedRef.current) {
            loadImage();
            observer && observer.disconnect();
          }
        }, { rootMargin: '200px' });
        observer.observe(containerRef.current);
      };

      // Small delay to ensure ref is attached
      const timeoutId = setTimeout(checkAndLoad, 0);
      
      return () => {
        isMounted = false;
        clearTimeout(timeoutId);
        if (observer) observer.disconnect();
        if (imageUrlRef.current) {
          window.URL.revokeObjectURL(imageUrlRef.current);
          imageUrlRef.current = null;
        }
      };
    }
  }, [filePath, downloadFn, loadImmediately]);

  if (loading) {
    return (
      <div ref={containerRef} className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800">
        <div className="animate-pulse text-gray-400 dark:text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error || !imageUrl) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800">
        <FileImage className="h-8 w-8 text-gray-400 dark:text-gray-600" />
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={fileName}
      className="w-full h-full object-cover"
    />
  );
};

export default ImageThumbnail;

